import {
  Switch,
  Route,
  Router as WouterRouter,
  Redirect,
  useLocation,
} from "wouter";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  useGetSession,
  getGetSessionQueryKey,
  useGetLegalStatus,
  getGetLegalStatusQueryKey,
  registerErrorInterceptor,
} from "@workspace/api-client-react";
import { emitPaywall, paywallPayloadFromBody } from "@/lib/paywall";
import { PaywallModal } from "@/components/PaywallModal";
import { ReactNode, Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { pageview } from "@/lib/analytics";
import { metaPageView, trackMetaEvent } from "@/lib/metaPixel";
import { captureUtmParams } from "@/lib/utm";
// Eager imports: MarketingPage is the homepage critical path (we want it
// in the initial bundle so first paint is fast and the static __SEO_BODY__
// snapshot is hydrated immediately). NotFound is tiny and used as a
// fallback target across the app — bundle cost is negligible.
import NotFound from "@/pages/not-found";
import MarketingPage from "@/pages/marketing/MarketingPage";
// Lazy imports: every non-marketing route is split into its own chunk so
// it does NOT bloat the homepage bundle. The biggest wins are the
// authenticated app pages (home, map, hunts, inbox, postcards, admin) —
// before this they were ~all bundled into the public marketing bundle
// shipped to first-time visitors. SEO pages are lazy too so each gets
// its own chunk per route.
const PrivacyPage = lazy(() => import("@/pages/legal/privacy"));
const TermsPage = lazy(() => import("@/pages/legal/terms"));
const DataDeletionPage = lazy(() => import("@/pages/legal/data-deletion"));
const AccountDeletionPage = lazy(() => import("@/pages/legal/account-deletion"));
const RefundPolicyPage = lazy(() => import("@/pages/legal/refund-policy"));
const AiDisclosurePage = lazy(() => import("@/pages/legal/ai-disclosure"));
const ChildrenFamilySafetyPage = lazy(() => import("@/pages/legal/children-family-safety"));
const TrustPage = lazy(() => import("@/pages/legal/trust"));
const SupportPage = lazy(() => import("@/pages/support"));
const FaqPage = lazy(() => import("@/pages/faq-page"));
const PricingPage = lazy(() => import("@/pages/pricing-page"));
const AboutPage = lazy(() => import("@/pages/about"));
const ContactPage = lazy(() => import("@/pages/contact"));
const HelpPage = lazy(() => import("@/pages/help"));
const SeoTopicPage = lazy(() => import("@/pages/marketing/seo/SeoTopicPage"));
const SignInPage = lazy(() => import("@/pages/sign-in"));
const CreatePage = lazy(() => import("@/pages/create"));
const JoinPage = lazy(() => import("@/pages/join"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordCodePage = lazy(() => import("@/pages/reset-password-code"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));
const HomePage = lazy(() => import("@/pages/home"));
const InboxPage = lazy(() => import("@/pages/inbox"));
const HuntsPage = lazy(() => import("@/pages/hunts"));
const HuntDetailPage = lazy(() => import("@/pages/hunt-detail"));
const MapPage = lazy(() => import("@/pages/map"));
const PostcardsPage = lazy(() => import("@/pages/postcards"));
const RecapPage = lazy(() => import("@/pages/recap"));
const MemoriesPage = lazy(() => import("@/pages/memories"));
const MemoriesSharePage = lazy(() => import("@/pages/memoriesShare"));
const AdminPage = lazy(() => import("@/pages/admin"));
const AdminFounderPage = lazy(() => import("@/pages/admin-founder"));
const MagicProfilePage = lazy(() => import("@/pages/magic-profile"));
const ProfilePage = lazy(() => import("@/pages/profile"));
const CheckoutSuccessPage = lazy(() => import("@/pages/checkout-success"));
const CheckoutCancelPage = lazy(() => import("@/pages/checkout-cancel"));
const LegalAcceptPage = lazy(() => import("@/pages/legal/legal-accept"));
const WishesPage = lazy(() => import("@/pages/wishes"));
const GiftWorkshopPage = lazy(() => import("@/pages/gift-workshop"));
const EmailNurturePage = lazy(() => import("@/pages/email-nurture"));
const NotificationsSettingsPage = lazy(() => import("@/pages/notifications-settings"));
// Dev-only token preview (Task #453). The DEV ternary lets Vite drop
// the dynamic import from production bundles.
const TokensPreviewPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/TokensPreview"))
  : null;
const AccountPrivacyPage = lazy(() => import("@/pages/account-privacy"));
const LaunchUpdatesPage = lazy(() => import("@/pages/marketing/LaunchUpdatesPage"));
const ThankYouPage = lazy(() => import("@/pages/marketing/ThankYouPage"));
const ChristmasMissionPage = lazy(() => import("@/pages/marketing/ChristmasMissionPage"));
const ChristmasEvePage = lazy(() => import("@/pages/marketing/ChristmasEvePage"));
import {
  SEO_PAGE_PATHS,
  SEO_SLUGS,
  type SeoSlug,
} from "@/pages/marketing/seo/content/manifest";
import {
  SEO_LOCALES,
  type SeoLocale,
} from "@/pages/marketing/seo/content/types";
import { MomentOverlay } from "@/components/MomentOverlay";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineSyncProvider } from "@/components/OfflineSyncProvider";
import { GuidanceProvider } from "@/components/GuidanceProvider";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

// Slugs that should fire a Meta Pixel `ViewContent` event. The user-facing
// spec lists English paths, but the same content exists in every locale —
// we fire ViewContent on entry to ANY localized variant of the same slug
// so paid social attribution is consistent across languages.
const META_PIXEL_VIEW_CONTENT_SLUGS: readonly SeoSlug[] = [
  "how-it-works",
  "family-scavenger-hunt",
  "christmas-eve-family-activity",
  "indoor-christmas-activity",
  "christmas-mission-app",
  "family-christmas-app",
] as const;

// Loading shell used as the Suspense fallback while a lazy route chunk
// loads. Matches the Protected/AdminGate loading state so auth-gated →
// app transitions don't flash a different background.
//
// Previously this was a completely empty `<div>` — visually identical to
// the inline-styled `#1a0608` body background and to the static SEO
// fallback snapshot (which is `display:none`). When debugging "blank dark
// red page on /legal/accept" we couldn't tell whether React had mounted
// at all or was stuck inside this Suspense fallback. Adding a small
// visible spinner + "Loading…" label disambiguates the two runtime
// states without changing semantics: pure presentational chrome,
// no extra hooks, no extra effects, no new behavior.
function RouteLoadingFallback() {
  return (
    <div
      className="dark relative min-h-screen bg-background text-foreground"
      data-testid="route-loading-fallback"
      role="status"
      aria-live="polite"
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <span
          aria-hidden
          className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-amber-200/30 border-t-amber-200"
        />
        <span className="text-xs uppercase tracking-[0.32em] text-amber-200/70">
          Loading…
        </span>
      </div>
    </div>
  );
}

// Global 402 → paywall interceptor.
//
// Two channels both feed into the same paywall event bus
// (artifacts/jingle/src/lib/paywall.ts):
//
//   1. Every orval-generated hook funnels its non-OK responses through
//      the registered `ErrorInterceptor` in @workspace/api-client-react,
//      so a 402 with `code: "entitlement_required"` is detected before
//      the hook's caller ever sees the rejected promise.
//   2. As a belt-and-braces safety net, the React Query `QueryCache`
//      and `MutationCache` `onError` callbacks inspect any thrown
//      `ApiError`'s `data` field — covers the case where a hook is
//      added later that bypasses the custom-fetch path.
//
// The PaywallModal mounted below subscribes to the bus and renders the
// upgrade prompt whenever an event fires.
registerErrorInterceptor((response, data) => {
  if (response.status !== 402) return;
  const payload = paywallPayloadFromBody(data);
  if (payload) emitPaywall(payload);
});

function maybeEmitPaywallFromError(err: unknown): void {
  if (!err || typeof err !== "object") return;
  const e = err as { status?: unknown; data?: unknown };
  if (e.status !== 402) return;
  const payload = paywallPayloadFromBody(e.data);
  if (payload) emitPaywall(payload);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  },
  queryCache: new QueryCache({
    onError: maybeEmitPaywallFromError,
  }),
  mutationCache: new MutationCache({
    onError: maybeEmitPaywallFromError,
  }),
});

// Paths that are always reachable even when a policy update is required.
// /legal/accept is exempted to avoid an infinite redirect loop.
// /logout, /account-deletion, /data-deletion are exempted so the user can
// always leave or delete their data without first re-accepting policies.
const POLICY_GATE_EXEMPT = [
  "/legal/accept",
  "/logout",
  "/account-deletion",
  "/data-deletion",
];

function Protected({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  const isAuthenticated = session.data?.authenticated === true;
  const mustReset = session.data?.mustResetPassword === true;
  const needsOnboarding = session.data?.needsOnboarding === true;
  const isExempt = POLICY_GATE_EXEMPT.some(
    (p) => location === p || location.startsWith(p + "/"),
  );
  const legalStatus = useGetLegalStatus({
    query: {
      queryKey: getGetLegalStatusQueryKey(),
      enabled: isAuthenticated && !mustReset && !isExempt,
      retry: false,
    },
  });

  if (session.isLoading) return <RouteLoadingFallback />;
  if (!isAuthenticated) return <Redirect to="/" />;
  // Force the password-reset flow before any in-app screen is reachable.
  if (mustReset) return <Redirect to="/reset-password" />;

  // Policy gate: block until we know the status, then redirect if needed.
  if (!isExempt) {
    if (legalStatus.isLoading) return <RouteLoadingFallback />;
    if (legalStatus.data !== undefined && !legalStatus.data.allClear) {
      return <Redirect to="/legal/accept" />;
    }
  }

  // Force the cinematic onboarding intro before any in-app screen is
  // reachable. Without this gate, a user whose `onboarding_complete` is
  // false (new account, or an account whose intro was reset) could land
  // straight on /home via a deep link, an existing session cookie, or a
  // refresh — bypassing the intro entirely.
  //
  // Two routes are excluded:
  //   • /onboarding itself, so we don't redirect onto ourselves.
  //   • Any POLICY_GATE_EXEMPT path (/legal/accept, /logout,
  //     /account-deletion, /data-deletion). These are designed as
  //     "always reachable" surfaces. Without this guard, a user with
  //     needsOnboarding === true AND legalStatus.allClear === false
  //     ping-pongs between /onboarding (Protected redirects to
  //     /legal/accept because !allClear) and /legal/accept (Protected
  //     redirects to /onboarding because needsOnboarding). Both
  //     `<Redirect>` calls render null, the matched <Switch> child
  //     renders null, nothing paints, and the user is left staring at
  //     the inline `html, body { background: #1a0608 }` — the "blank
  //     dark red" reported on /legal/accept. Exempting the same paths
  //     that already bypass the legal gate here breaks the loop
  //     without weakening any actual gate (legal acceptance and
  //     onboarding completion are both still enforced — just not in a
  //     way that can deadlock when both are pending).
  if (needsOnboarding && location !== "/onboarding" && !isExempt) {
    return <Redirect to="/onboarding" />;
  }

  return <>{children}</>;
}

function AdminGate({ children }: { children: ReactNode }) {
  // Mirrors Protected, but additionally bounces non-admins back to /home so
  // they never see the God-mode UI. The server still enforces admin on every
  // /api/admin/* call — this is purely a UX gate.
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  if (session.isLoading) return <RouteLoadingFallback />;
  if (!session.data?.authenticated) return <Redirect to="/" />;
  if (session.data?.mustResetPassword) return <Redirect to="/reset-password" />;
  if (!session.data?.isAdmin) return <Redirect to="/home" />;
  return <>{children}</>;
}

// Task #156 — Family Safety + Kid Mode v1. A child profile must never
// reach the founder ops surface (server returns 404 anyway, but we
// bounce client-side so the URL is invisible). All other Protected
// routes remain reachable so the child can see Home/Inbox/Map/etc.
function NoChildGate({ children }: { children: ReactNode }) {
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  if (session.isLoading) return <RouteLoadingFallback />;
  if (!session.data?.authenticated) return <Redirect to="/" />;
  if (session.data?.mustResetPassword) return <Redirect to="/reset-password" />;
  if (session.data?.role === "child") return <Redirect to="/home" />;
  return <>{children}</>;
}

function ResetGate({ children }: { children: ReactNode }) {
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  if (session.isLoading) return <RouteLoadingFallback />;
  if (!session.data?.authenticated) return <Redirect to="/" />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  if (session.isLoading) return <RouteLoadingFallback />;
  if (session.data?.authenticated) return <Redirect to="/home" />;
  return <>{children}</>;
}

// Native (Capacitor) cold-start lands on `/`. The marketing homepage
// is not shipped in native builds (see __NATIVE_BUILD__ guard on
// MarketingPage), so the root route uses this component:
//   - authenticated: bounce into the in-app shell at /home (Task #440)
//   - anonymous: render the friendly NotFound surface (Task #451) so
//     the native fallback contract enforced by
//     verifyNativeRouteFallback.ts holds at `/` too. NotFound's CTA
//     links back to `/`, which simply re-renders NotFound — a benign
//     loop, never a blank screen. Anon users reach SignInPage via the
//     "Sign in" entry points elsewhere in the shell, not via `/`.
function NativeRoot() {
  const session = useGetSession({
    query: { queryKey: getGetSessionQueryKey(), retry: false },
  });
  if (session.isLoading) return <RouteLoadingFallback />;
  if (session.data?.authenticated) return <Redirect to="/home" />;
  return <NotFound />;
}

function Router() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
    <Switch>
      {/* Native (Capacitor iOS / Android) builds ship without the public
          marketing/SEO/founder-ops surfaces — those pages already render
          null in native via `if (__NATIVE_BUILD__) return null;` (see
          MarketingPage / admin-founder / email-nurture / etc.). Skipping
          the <Route> entries themselves means a stray in-app link to one
          of those paths falls through to NotFound instead of landing on
          a blank screen. Task #440. */}
      {!__NATIVE_BUILD__ && (
        // Public marketing homepage. Renders for everyone — anonymous
        // visitors AND signed-in players. We intentionally do NOT wrap
        // this in <PublicOnly>: that would auto-redirect signed-in
        // users to /home and effectively hide the public landing page
        // from them (returning to the brand surface, sharing the link
        // with family, hitting the production root, etc. would all
        // bounce into the app). Signed-in users reach /home via the
        // bottom nav, the "Open the app" CTA in the marketing top nav,
        // or any of the in-app deep links. The marketing page itself
        // never calls any auth-gated APIs, so showing it to a signed-in
        // user is safe.
        <Route path="/">
          <MarketingPage />
        </Route>
      )}
      {/* Native cold-start lands on `/`. There's no marketing page in
          the Capacitor binary, so the root route is handled by
          <NativeRoot />: authenticated users redirect to /home (Task
          #440); anonymous users render the friendly NotFound surface
          (Task #451) so the native fallback contract enforced by
          verifyNativeRouteFallback.ts holds at `/` too. */}
      {__NATIVE_BUILD__ && (
        <Route path="/">
          <NativeRoot />
        </Route>
      )}
      {/* Dev-only token preview (Task #453). Only registered when
          `import.meta.env.DEV` so the route 404s in production builds
          and the preview page never ships to end users. */}
      {import.meta.env.DEV && TokensPreviewPage && (
        <Route path="/dev/tokens">
          <TokensPreviewPage />
        </Route>
      )}
      <Route path="/privacy">
        <PrivacyPage />
      </Route>
      <Route path="/terms">
        <TermsPage />
      </Route>
      <Route path="/data-deletion">
        <DataDeletionPage />
      </Route>
      <Route path="/account-deletion">
        <AccountDeletionPage />
      </Route>
      <Route path="/refund-policy">
        <RefundPolicyPage />
      </Route>
      <Route path="/ai-disclosure">
        <AiDisclosurePage />
      </Route>
      <Route path="/ai-summary">
        <Redirect to="/ai-disclosure" />
      </Route>
      <Route path="/children-family-safety">
        <ChildrenFamilySafetyPage />
      </Route>
      <Route path="/trust">
        <TrustPage />
      </Route>
      <Route path="/support">
        <SupportPage />
      </Route>
      <Route path="/faq">
        <FaqPage />
      </Route>
      {!__NATIVE_BUILD__ && (
        <Route path="/pricing">
          <PricingPage />
        </Route>
      )}
      {!__NATIVE_BUILD__ && (
        <Route path="/about">
          <AboutPage />
        </Route>
      )}
      <Route path="/contact">
        <ContactPage />
      </Route>
      <Route path="/help">
        <HelpPage />
      </Route>
      {!__NATIVE_BUILD__ &&
        SEO_SLUGS.flatMap((slug: SeoSlug) =>
          SEO_LOCALES.map((locale: SeoLocale) => (
            <Route
              key={`${slug}-${locale}`}
              path={SEO_PAGE_PATHS[slug][locale]}
            >
              <SeoTopicPage slug={slug} locale={locale} />
            </Route>
          )),
        )}
      {!__NATIVE_BUILD__ && (
        <Route path="/launch-updates">
          <PublicOnly>
            <LaunchUpdatesPage />
          </PublicOnly>
        </Route>
      )}
      {!__NATIVE_BUILD__ && (
        <Route path="/thank-you">
          <ThankYouPage />
        </Route>
      )}
      {!__NATIVE_BUILD__ && (
        <Route path="/christmas-mission">
          <ChristmasMissionPage />
        </Route>
      )}
      {!__NATIVE_BUILD__ && (
        <Route path="/christmas-eve">
          <ChristmasEvePage />
        </Route>
      )}
      <Route path="/login">
        <PublicOnly>
          <SignInPage />
        </PublicOnly>
      </Route>
      <Route path="/sign-in">
        <Redirect to="/login" />
      </Route>
      <Route path="/create">
        <CreatePage />
      </Route>
      <Route path="/join">
        <JoinPage />
      </Route>
      <Route path="/reset-password">
        <ResetGate>
          <ResetPasswordPage />
        </ResetGate>
      </Route>
      <Route path="/forgot-password">
        <ForgotPasswordPage />
      </Route>
      <Route path="/reset-password/code">
        <ResetPasswordCodePage />
      </Route>
      <Route path="/onboarding">
        <Protected>
          <OnboardingPage />
        </Protected>
      </Route>
      <Route path="/home">
        <Protected>
          <HomePage />
        </Protected>
      </Route>
      <Route path="/inbox">
        <Protected>
          <InboxPage />
        </Protected>
      </Route>
      <Route path="/inbox/:id">
        <Protected>
          <InboxPage />
        </Protected>
      </Route>
      <Route path="/hunts">
        <Protected>
          <HuntsPage />
        </Protected>
      </Route>
      <Route path="/hunts/:id">
        <Protected>
          <HuntDetailPage />
        </Protected>
      </Route>
      <Route path="/map">
        <Protected>
          <MapPage />
        </Protected>
      </Route>
      <Route path="/postcards">
        <Protected>
          <PostcardsPage />
        </Protected>
      </Route>
      <Route path="/recap">
        <Protected>
          <RecapPage />
        </Protected>
      </Route>
      <Route path="/memories/share/:token">
        {/* Public, token-gated. No <Protected> wrapper — guests reach
            this via the share link the host minted. */}
        <MemoriesSharePage />
      </Route>
      <Route path="/memories">
        <Protected>
          <MemoriesPage />
        </Protected>
      </Route>
      <Route path="/wishes">
        <Protected>
          <WishesPage />
        </Protected>
      </Route>
      <Route path="/gift-workshop">
        <Protected>
          <GiftWorkshopPage />
        </Protected>
      </Route>
      <Route path="/settings/notifications">
        <Protected>
          <NotificationsSettingsPage />
        </Protected>
      </Route>
      {/* Task #244 — Self-Serve Privacy & Deletion Ops v1.
          Apple App Store Guideline 5.1.1(v): in-app account deletion path.
          Route is Protected (must be logged in). No child gate needed —
          child profiles see the page but cannot submit family_deletion
          (enforced both server-side and in the UI). */}
      <Route path="/account/privacy">
        <Protected>
          <AccountPrivacyPage />
        </Protected>
      </Route>
      <Route path="/admin">
        <AdminGate>
          <AdminPage />
        </AdminGate>
      </Route>
      <Route path="/magic-profile">
        <AdminGate>
          <MagicProfilePage />
        </AdminGate>
      </Route>
      {/* Task #462 — Next-gen Profile & Family settings hub.
          Re-skin only: composes existing data hooks (useGetMyTeam,
          useListElves, useGetSession) into a premium identity surface.
          All settings rows route to existing pages — no new endpoints. */}
      <Route path="/profile">
        <Protected>
          <ProfilePage />
        </Protected>
      </Route>
      {/* /admin/founder is gated server-side: all /api/founder/* routes
          return 404 to non-founders, and the page renders the same
          NotFound component on a 404 response — so a non-founder never
          learns this route exists. Native builds also drop the route
          entirely (the page already returns null in native) so a stray
          link falls through to NotFound instead of a blank screen. */}
      {!__NATIVE_BUILD__ && (
        <Route path="/admin/founder">
          <NoChildGate>
            <AdminFounderPage />
          </NoChildGate>
        </Route>
      )}
      {/* Task #369 — Email Nurture portal. Same server-side gate as
          /admin/founder: all /api/founder/nurture/* routes return 404 to
          non-founders. Native builds drop the route entirely. */}
      {!__NATIVE_BUILD__ && (
        <Route path="/admin/email-nurture">
          <NoChildGate>
            <EmailNurturePage />
          </NoChildGate>
        </Route>
      )}
      {/* Task #121 — Stripe sandbox checkout return URLs. /checkout/success
          re-verifies via /api/payments/checkout/status (we never trust
          the URL). /checkout/cancel is a graceful no-op. Both require
          a session because /status is auth-gated. */}
      <Route path="/checkout/success">
        <Protected>
          <CheckoutSuccessPage />
        </Protected>
      </Route>
      <Route path="/checkout/cancel">
        <CheckoutCancelPage />
      </Route>
      <Route path="/legal/accept">
        <Protected>
          <LegalAcceptPage />
        </Protected>
      </Route>
      <Route component={NotFound} />
    </Switch>
    </Suspense>
  );
}

// Fires GA4 + Meta Pixel page_view on every wouter location change AFTER
// the initial mount. The first pageview is already emitted by the
// gtag('config', GA_ID) call AND the inline fbq('track','PageView') call
// in index.html — firing again on first mount would double-count the
// landing page. Also fires Meta Pixel `ViewContent` whenever the user
// enters one of the tracked SEO/product pages (homepage + the explicit
// slugs from META_PIXEL_VIEW_CONTENT_SLUGS, across all locales). This
// component renders nothing.
function AnalyticsPageviews() {
  const [location] = useLocation();
  const isFirstRef = useRef(true);

  // Build the ViewContent path set once. Includes "/" plus every locale
  // variant of every tracked slug — so /how-it-works AND /fr/how-it-works
  // AND /pa/how-it-works all fire the same ViewContent.
  const viewContentPaths = useMemo<ReadonlySet<string>>(
    () =>
      new Set<string>([
        "/",
        ...META_PIXEL_VIEW_CONTENT_SLUGS.flatMap((slug) =>
          SEO_LOCALES.map((locale: SeoLocale) => SEO_PAGE_PATHS[slug][locale]),
        ),
      ]),
    [],
  );

  useEffect(() => {
    // Attribution capture runs on every route change. On the first
    // landing it seeds first_touch (the value used to enrich every
    // event), and on subsequent route changes it updates latest_touch
    // only when new ad-click params appear in the URL. Safe to call
    // every time — idempotent by design.
    captureUtmParams();
    if (isFirstRef.current) {
      isFirstRef.current = false;
    } else {
      pageview();      // GA4 page_view (SPA route change)
      metaPageView();  // Meta Pixel PageView (SPA route change)
    }
    // ViewContent fires on every entry to a tracked content page,
    // INCLUDING the first mount — ViewContent is independent of PageView
    // and the inline fbq('track','PageView') in index.html does not
    // emit it. The pixel script is initialized synchronously in <head>
    // so window.fbq exists by the time React renders this effect.
    if (viewContentPaths.has(location)) {
      trackMetaEvent("ViewContent", { content_name: location });
    }
  }, [location, viewContentPaths]);
  return null;
}

// Routes native deep links (invite codes, password reset tokens) into Wouter
// when JINGLE is running as a Capacitor iOS/Android app. The `appUrlOpen`
// event fires whenever the OS routes a URL to the native shell — this
// happens for Universal Links (iOS) and App Links (Android) once the AASA /
// assetlinks.json files are configured by the founder.
//
// In a browser build `Capacitor.isNativePlatform()` is false so this
// component is a complete no-op — no listener is registered, no import
// penalty beyond the Capacitor core tree-shake.
//
// AASA file: public/.well-known/apple-app-site-association (placeholder —
// founder must fill in Apple Team ID before Universal Links activate).
function CapacitorDeepLinkHandler() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => void } | null = null;

    CapApp.addListener("appUrlOpen", (data: { url: string }) => {
      try {
        const url = new URL(data.url);
        const path = url.pathname + url.search + url.hash;
        // Push the path into Wouter's history so the SPA Router component
        // picks it up without a full WebView reload.
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch {
        // Malformed URL — ignore silently.
      }
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, []);

  return null;
}

// Scrolls the window to the top on every wouter route change so SPA
// navigations (Footer "Explore JINGLE" links, "Keep exploring" related
// links at the bottom of SEO pages, etc.) don't strand the user at the
// previous page's scroll position — which is what the browser does by
// default for pushState navigation, and is especially jarring on mobile.
//
// Skips when the URL has a hash so in-page anchor links still scroll to
// the targeted section. Skips on the initial mount because the browser
// already starts the page at the top (or restores the user's prior
// position on back/forward, which we want to preserve).
// Cancels orphaned in-flight React Query requests on every wouter route
// change. React Query queries are already AbortController-aware — every
// generated hook funnels through a `customFetch` that forwards the
// `signal` provided by React Query — so calling `cancelQueries()` here
// triggers `signal.abort()` on the underlying `fetch`, which closes
// the HTTP connection. The previous behaviour was that a slow list
// query on `/inbox` would keep streaming bytes after the user navigated
// to `/map`, wasting bandwidth on mobile and forcing the new route to
// compete for the network.
//
// IMPORTANT: this effect runs AFTER the new route has already mounted,
// so any active observers belong to the destination route (or to
// app-shell hooks like useGetSession / useGetLegalStatus that span
// every page). We must NOT abort those — that would blank the new
// page on every navigation. The `type: "inactive"` filter targets
// only queries that currently have zero observers, which is exactly
// the set of queries the previous route was using and that nothing
// else is still watching. Skips the initial mount because no requests
// can possibly be in flight yet.
function CancelInFlightOnRouteChange() {
  const [location] = useLocation();
  const isFirstRef = useRef(true);
  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return;
    }
    // `cancelQueries` returns a Promise; we deliberately don't await
    // it — abort signals are dispatched synchronously inside the call.
    void queryClient.cancelQueries({ type: "inactive" });
  }, [location]);
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  const isFirstRef = useRef(true);
  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return;
    }
    if (typeof window === "undefined") return;
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            {/* GuidanceProvider sits above the router so route changes can't
                remount it and lose in-progress tour state. It is zero-cost
                when no tour is open — pure React state, no effects, no
                network calls. */}
            <GuidanceProvider>
              {/* Phase F3 — single mount point for the offline-find sync
                  coordinator. Lives ABOVE the Switch so route changes can't
                  remount it (which would re-attach the `online` listener
                  and start a parallel drain loop). Inner components read
                  pendingCount/syncNow via useOfflineSyncContext(). */}
              <OfflineSyncProvider>
                <AnalyticsPageviews />
                <ScrollToTop />
                <CancelInFlightOnRouteChange />
                <CapacitorDeepLinkHandler />
                <Router />
                <MomentOverlay />
                <PaywallModal />
              </OfflineSyncProvider>
            </GuidanceProvider>
          </WouterRouter>
        </ErrorBoundary>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
